// Domain-aware SEO Configuration
// This file contains all SEO metadata for different domains and routes

import {
  isLocalDevelopment,
  isCountrySpecific,
  getCountryFromDomain as getCountryFromDomainUtil,
  DOMAIN_CONFIG,
} from './domainUtils'
import { SEO_DATA } from './seoData'

/**
 * Detects the current domain from window.location
 * Falls back to gcctaxlaws.com if domain is not recognized
 */
export const getCurrentDomain = () => {
  if (typeof window === 'undefined') return DOMAIN_CONFIG.PRODUCTION

  const hostname = window.location.hostname

  // Handle localhost and development environments
  if (isLocalDevelopment(hostname)) {
    return DOMAIN_CONFIG.PRODUCTION
  }

  // Remove 'www.' prefix if present
  const normalizedHostname = hostname.startsWith('www.')
    ? hostname.substring(4)
    : hostname

  // Handle subdomain cases (chatbot, payments, search)
  if (normalizedHostname.includes('.')) {
    const parts = normalizedHostname.split('.')
    // If it's a subdomain like chatbot.uaetaxlaws.com, extract the main domain
    if (parts.length > 2) {
      const mainDomain = parts.slice(-2).join('.')
      // Check if this is a valid domain
      if (
        DOMAIN_CONFIG.COUNTRY_SPECIFIC.includes(mainDomain) ||
        mainDomain === DOMAIN_CONFIG.PRODUCTION
      ) {
        return mainDomain
      }
    }
  }

  // Return normalized hostname if it's in our valid domains
  if (
    isCountrySpecific(normalizedHostname) ||
    normalizedHostname === DOMAIN_CONFIG.PRODUCTION
  ) {
    return normalizedHostname
  }

  // Fallback to production domain
  return DOMAIN_CONFIG.PRODUCTION
}

/**
 * Gets the country code from domain
 * Used for filtering content and routes
 */
export const getCountryFromDomain = (domain = null) => {
  const currentDomain = domain || getCurrentDomain()

  // Use the utility function for country-specific domains
  const country = getCountryFromDomainUtil(currentDomain)

  // Return country if found, otherwise return 'GCC' for production domain
  return country || 'GCC'
}

/**
 * Gets the base URL for the current domain
 */
export const getBaseUrl = (domain = null) => {
  const currentDomain = domain || getCurrentDomain()
  return `https://${currentDomain}`
}

/**
 * Gets SEO data for a specific route on the current domain
 * @param {string} pathname - The current route pathname
 * @param {string} domain - Optional domain override
 * @returns {object} SEO data object with title, description, keywords, h1, h2
 */
export const getSEOForRoute = (pathname, domain = null) => {
  const currentDomain = domain || getCurrentDomain()
  const domainData =
    SEO_DATA[currentDomain] || SEO_DATA[DOMAIN_CONFIG.PRODUCTION]

  // Direct match
  if (domainData[pathname]) {
    return {
      ...domainData[pathname],
      canonical: `${getBaseUrl(currentDomain)}${pathname}`,
    }
  }

  // Pattern matching for dynamic routes
  // Handle /laws/:country/:lawShortName routes
  const lawMatch = pathname.match(/^\/laws\/([^/]+)\/([^/]+)$/)
  if (lawMatch) {
    const [, country, lawShortName] = lawMatch
    const routeKey = `/laws/${country.toLowerCase()}/${lawShortName}`
    if (domainData[routeKey]) {
      return {
        ...domainData[routeKey],
        canonical: `${getBaseUrl(currentDomain)}${pathname}`,
      }
    }
  }

  // Handle /search/:country/Tax-Treaties routes
  const treatyMatch = pathname.match(/^\/search\/([^/]+)\/Tax-Treaties$/)
  if (treatyMatch) {
    const [, country] = treatyMatch
    const routeKey = `/search/${country}/Tax-Treaties`
    if (domainData[routeKey]) {
      return {
        ...domainData[routeKey],
        canonical: `${getBaseUrl(currentDomain)}${pathname}`,
      }
    }
  }

  // Handle /news/:country routes
  const newsMatch = pathname.match(/^\/news\/([^/]+)$/)
  if (newsMatch) {
    const [, country] = newsMatch
    const routeKey = `/news/${country.toLowerCase()}`
    if (domainData[routeKey]) {
      return {
        ...domainData[routeKey],
        canonical: `${getBaseUrl(currentDomain)}${pathname}`,
      }
    }
  }

  // Fallback to homepage SEO
  return {
    ...domainData['/'],
    canonical: `${getBaseUrl(currentDomain)}${pathname}`,
  }
}

/**
 * Checks if a law is available on the current domain
 * GCC common laws are available on all domains
 * Country-specific laws are only available on their respective domain
 */
export const isLawAvailableOnDomain = (lawShortName, domain = null) => {
  const currentDomain = domain || getCurrentDomain()
  const country = getCountryFromDomain(currentDomain)

  // GCC common laws available everywhere
  const gccCommonLaws = [
    'common-customs-agreement',
    'common-vat-agreement',
    'common-excise-agreement',
  ]

  if (gccCommonLaws.includes(lawShortName)) {
    return true
  }

  // GCC domain shows all laws
  if (country === 'GCC') {
    return true
  }

  // Country-specific check
  // Extract country from lawShortName or use mapping
  const lawCountryMap = {
    'cit-fdl-47-of-2022': 'UAE',
    'dmtt-fdl-60-of-2023': 'UAE',
    'indirecttax-law-vat-uaevat-8-of-2017-as-amended': 'UAE',
    'tp-fdl-28-of-2022-as-amended': 'UAE',
    'indirect-excise-7-of-2017-as-amended': 'UAE',
    'fatca-of-2024': 'UAE',
    'crs-federal-decree-48-of-2018': 'UAE',
    'income-tax-law-of-2004': 'KSA',
    'vat-law-m-113-of-2017': 'KSA',
    'fatca-of-2017': 'KSA',
    'fatca-of-2014-iga-m1': 'Qatar',
    'fatca-of-2014-direct': 'Oman',
    'zakat-law-of-1951': 'KSA',
    'kit-law-no-2-of-2008-amending-decree-no-3-of-1955': 'Kuwait',
    'qdmtt-decree-law-157-of-2024': 'Kuwait',
    'dmtt-fdl-22-of-2024': 'Qatar',
    'draft-business-profit-tax-law-of-2024': 'Kuwait',
    'income-tax-law-24-of-2018': 'Qatar',
    'excise-law-25-of-2018': 'Qatar',
    'income-tax-law-royal-decree-28-of-2009': 'Oman',
    'vat-law-royal-decree-121-of-2020': 'Oman',
    'excise-law-of-2019': 'KSA',
    'excise-tax-law-royal-decree-23-of-2019': 'Oman',
    'dmtt-law-11-of-2024': 'Bahrain',
    'vat-law-48-of-2018': 'Bahrain',
  }

  const lawCountry = lawCountryMap[lawShortName]
  return lawCountry === country
}

/**
 * Helper function to get all available domains
 * Useful for building sitemaps or navigation
 */
export const getAllDomains = () => {
  return [DOMAIN_CONFIG.PRODUCTION, ...DOMAIN_CONFIG.COUNTRY_SPECIFIC]
}

/**
 * Helper to check if current domain should show country-specific content
 * @returns {boolean}
 */
export const shouldShowCountryContent = () => {
  const domain = getCurrentDomain()
  return isCountrySpecific(domain)
}

/**
 * Helper to get domain display name
 * @param {string} domain - Domain to get display name for
 * @returns {string} Display name
 */
export const getDomainDisplayName = (domain = null) => {
  const currentDomain = domain || getCurrentDomain()
  const country = getCountryFromDomain(currentDomain)
  if (country === 'GCC') {
    return 'GCC Tax Laws'
  }
  return `${country} Tax Laws`
}

export default {
  getCurrentDomain,
  getCountryFromDomain,
  getBaseUrl,
  getSEOForRoute,
  isLawAvailableOnDomain,
  getAllDomains,
  shouldShowCountryContent,
  getDomainDisplayName,
  SEO_DATA,
  DOMAIN_CONFIG,
}
